import { createRoot } from 'react-dom/client';
import React from 'react';
import WindCreekClient from '../../../src/scripts/user-account/WindCreekClient.mjs';
import { getCurrentDate, getDateString, getEndDate } from './dateHelpers.mjs';
import Event from './Event.mjs';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorPlaceholder />;
    }

    return this.props.children;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const appDiv = document.getElementById('upcomingApp');
  const calendarType = appDiv.dataset.calendarType;
  const propertyCode = (WindCreekData.propertyCode === 'WCC') ? 'WCI' : WindCreekData.propertyCode;

  const startDate = getDateString(getCurrentDate());
  const endDate = getDateString(getEndDate());

  let calendarTypeId = null;
  switch (calendarType) {
    case 'LIVE_ENTERTAINMENT':
      calendarTypeId = 3;
      break;
    case 'NIGHTLIFE':
      calendarTypeId = 4;
      break;
    default:
      calendarTypeId = null;
  }

  const root = createRoot(appDiv);

  // API & Promos
  const wcc = new WindCreekClient();
  const queryBody = {
    operationName: "GetCalendar",
    variables: {
      input: {
        property: propertyCode,
        startDate: startDate,
        endDate: endDate,
        showUnpublishedEvents: false,
        showEmployeeOnlyEvents: null,
        calendarTypeId: calendarTypeId,
      }
    },
    query: `query GetCalendar($input: GetCommunityEventsCalendar!) {
      communityEventsCalendar(input: $input) {
        calendarDays {
          date
          eventIds
          __typename
        }
        events {
          eventId
          name
          thumbnailImageUrl
          detailsUrl
        }
        __typename
      }
    }`
  };

  wcc.query(queryBody)
    .then(response => {
      const data = response.data.communityEventsCalendar;
      const calendarDays = data.calendarDays;
      const events = data.events;

      const mappedCalendarDays = calendarDays.map(day => {
        if (events) {
          let dayEvents = day.eventIds.map(eventId =>
            events.find(event => event.eventId === eventId)
          );

          return { ...day, events: dayEvents };
        }

        return false;
      });

      const first3UniqueEvents = getFirst3UniqueEvents(mappedCalendarDays);

      root.render(
        <ErrorBoundary>
          <App
            events={first3UniqueEvents}
          />
        </ErrorBoundary>
      );
    })
    .catch(error => {
      console.error('Error:', error);

      root.render(
        <ErrorBoundary>
          <ErrorPlaceholder />
        </ErrorBoundary>
      );
    });
});

function App({ events }) {
  if (events.length > 0) {
    const eventDivs = events.map(event => {
      return (
        <Event
          key={'event-' + event.eventId}
          title={event.name}
          imgSrc={event.thumbnailImageUrl}
          url={event.detailsUrl}
        />
      );
    });

    return (
      <div className="row">
        {eventDivs}
      </div>
    );
  } else {
    return <NoEventsPlaceholder />;
  }
}

function ErrorPlaceholder() {
  return (
    <div className="bg-white card-promo rounded shadow px-2 py-3 p-md-3 text-align-center">
      <p className="subhead-1 text-primary">
        Unable to load events
      </p>
      <p className="text-dark-subtle mb-1">
        Please check back soon.
      </p>
    </div>
  );
}

function NoEventsPlaceholder() {
  return (
    <div className="bg-white card-promo rounded shadow px-2 py-3 p-md-3 text-align-center">
      <p className="subhead-1 text-primary">
        No upcoming events
      </p>
      <p className="text-dark-subtle mb-1">
        Please check back soon.
      </p>
    </div>
  );
}

function getFirst3UniqueEvents(events) {
  const uniqueEvents = [];
  const seenEventIds = new Set();

  for (const day of events) {
    for (const event of day.events) {
      if (!seenEventIds.has(event.eventId)) {
        uniqueEvents.push(event);
        seenEventIds.add(event.eventId);
        if (uniqueEvents.length === 3) {
          return uniqueEvents;
        }
      }
    }
  }

  return uniqueEvents;
}
